import React, { Children } from 'react';
import { Star, CheckCircle2, MapPin, ArrowRight, Clock, Scale, Calculator } from 'lucide-react';
import { motion } from 'framer-motion';
export function HeroSection() {
  return <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 px-6 bg-gradient-to-br from-blue-deep via-blue to-navy overflow-hidden" data-id="element-215">
      {/* Playful Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-216">
        <div className="absolute top-10 left-10 w-64 h-64 bg-teal/20 rounded-full mix-blend-screen filter blur-3xl animate-blob" data-id="element-217"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-coral/20 rounded-full mix-blend-screen filter blur-3xl animate-blob" style={{
        animationDelay: '2s'
      }} data-id="element-218"></div>
        <div className="absolute -bottom-20 left-1/3 w-80 h-80 bg-yellow/20 rounded-full mix-blend-screen filter blur-3xl animate-blob" style={{
        animationDelay: '4s'
      }} data-id="element-219"></div>

        {/* SVG Pattern Overlay */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg" data-id="element-220">
          <pattern id="pattern-circles" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-221">
            <circle cx="20" cy="20" r="2" fill="#FFFFFF" data-id="element-222" />
          </pattern>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#pattern-circles)" data-id="element-223" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12 lg:gap-16 relative z-10" data-id="element-224">
        {/* Left Content */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="flex-1 w-full flex flex-col gap-8" data-id="element-225">
          {/* Trust Chips */}
          <div className="flex flex-wrap gap-3" data-id="element-226">
            <div className="flex items-center gap-1.5 bg-yellow px-4 py-1.5 rounded-full text-sm font-bold text-navy shadow-sm" data-id="element-227">
              <Star size={14} className="fill-navy" data-id="element-228" />
              <span data-id="element-229">4.0/5 CareforKids (81 reviews)</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-semibold text-white border border-white/20" data-id="element-230">
              <CheckCircle2 size={14} className="text-teal" data-id="element-231" />
              <span data-id="element-232">Meeting NQS</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-semibold text-white border border-white/20" data-id="element-233">
              <MapPin size={14} className="text-coral" data-id="element-234" />
              <span data-id="element-235">Wyndham Vale VIC 3024</span>
            </div>
          </div>

          <div className="flex flex-col gap-4" data-id="element-236">
            <h1 className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] font-extrabold text-white tracking-tight" data-id="element-237">
              Childcare in Wyndham Vale,{' '}
              <span className="text-yellow block mt-2" data-id="element-238">
                $118/day{' '}
                <span className="text-3xl md:text-4xl lg:text-5xl text-coral" data-id="element-239">
                  - from around $24 with CCS.
                </span>
              </span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl font-medium mt-2" data-id="element-240">
              Family-run early learning for children aged 6 weeks to
              kindergarten. Open 6:30am-6:30pm. CCS approved. Tours this week.
            </p>
          </div>

          {/* Primary CTAs */}
          <div className="flex flex-col sm:flex-row items-center gap-4 mt-2" data-id="element-241">
            <a href="#tours" className="w-full sm:w-auto bg-coral text-white px-8 py-4 rounded-btn font-bold text-lg hover:bg-red transition-colors shadow-lg text-center animate-pulse-cta" data-id="element-242">
              Book a tour this week
            </a>
            <a href="tel:0387143350" className="w-full sm:w-auto bg-white/10 backdrop-blur-md text-white border border-white/30 px-8 py-4 rounded-btn font-bold text-lg hover:bg-white/20 transition-colors text-center" data-id="element-243">
              Call 03 8714 3350
            </a>
          </div>

          {/* Merged Parent Triage (Micro-conversions) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 pt-6 border-t border-white/10" data-id="element-244">
            <a href="#availability" className="group flex items-center gap-3 bg-white/5 hover:bg-white/10 p-3 rounded-xl transition-colors border border-white/5" data-id="element-245">
              <div className="w-8 h-8 rounded-full bg-teal/20 flex items-center justify-center text-teal shrink-0" data-id="element-246">
                <Clock size={16} data-id="element-247" />
              </div>
              <span className="text-sm font-semibold text-white/90 group-hover:text-white flex-1" data-id="element-248">
                Need care soon?
              </span>
              <ArrowRight size={16} className="text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" data-id="element-249" />
            </a>
            <a href="#tours" className="group flex items-center gap-3 bg-white/5 hover:bg-white/10 p-3 rounded-xl transition-colors border border-white/5" data-id="element-250">
              <div className="w-8 h-8 rounded-full bg-coral/20 flex items-center justify-center text-coral shrink-0" data-id="element-251">
                <Scale size={16} data-id="element-252" />
              </div>
              <span className="text-sm font-semibold text-white/90 group-hover:text-white flex-1" data-id="element-253">
                Comparing centres?
              </span>
              <ArrowRight size={16} className="text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" data-id="element-254" />
            </a>
            <a href="#ccs-calculator" className="group flex items-center gap-3 bg-white/5 hover:bg-white/10 p-3 rounded-xl transition-colors border border-white/5" data-id="element-255">
              <div className="w-8 h-8 rounded-full bg-yellow/20 flex items-center justify-center text-yellow shrink-0" data-id="element-256">
                <Calculator size={16} data-id="element-257" />
              </div>
              <span className="text-sm font-semibold text-white/90 group-hover:text-white flex-1" data-id="element-258">
                Understand CCS?
              </span>
              <ArrowRight size={16} className="text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" data-id="element-259" />
            </a>
          </div>
        </motion.div>

        {/* Right Visual */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="flex-1 w-full relative" data-id="element-260">
          {/* Playful dots around the image */}
          <div className="absolute -top-6 -right-6 w-12 h-12 bg-yellow rounded-full mix-blend-screen opacity-70 animate-blob" data-id="element-261"></div>
          <div className="absolute top-1/2 -left-8 w-16 h-16 bg-teal rounded-full mix-blend-screen opacity-60 animate-blob" style={{
          animationDelay: '1s'
        }} data-id="element-262"></div>
          <div className="absolute -bottom-8 right-12 w-20 h-20 bg-coral rounded-full mix-blend-screen opacity-60 animate-blob" style={{
          animationDelay: '2s'
        }} data-id="element-263"></div>

          <div className="aspect-[4/5] md:aspect-square lg:aspect-[4/5] bg-sand rounded-[32px] overflow-hidden relative border-4 border-white/20 shadow-2xl flex items-center justify-center" data-id="element-264">
            <img src="https://cdn.magicpatterns.com/uploads/i93NFP9TDRpFm3tk93ov5M/creative-kids-posing.jpg" alt="Children with painted hands having fun at Funtastic Kidz" className="absolute inset-0 w-full h-full object-cover" data-id="element-265" />
            {/* Warm overlay */}
            <div className="absolute inset-0 bg-blue-deep/20 mix-blend-overlay" data-id="element-266"></div>
            <div className="absolute inset-0 bg-cream/10" data-id="element-267"></div>
          </div>

          {/* Floating Cards - Hidden on mobile to prevent overflow, visible on md+ */}
          <div className="hidden md:block absolute top-8 -left-4 bg-white p-4 rounded-2xl shadow-xl border-l-4 border-teal animate-float z-20" data-id="element-268">
            <p className="font-extrabold text-navy text-lg whitespace-nowrap" data-id="element-269">
              Open 6:30am-6:30pm
            </p>
          </div>

          <div className="hidden md:block absolute bottom-24 -right-4 bg-white p-5 rounded-2xl shadow-xl border-t-4 border-coral animate-float z-20" style={{
          animationDelay: '-2s'
        }} data-id="element-270">
            <p className="font-heading font-extrabold text-3xl text-navy" data-id="element-271">
              $118<span className="text-lg text-navy/60" data-id="element-272">/day</span>
            </p>
            <p className="text-sm font-bold text-coral mt-1" data-id="element-273">
              from $24 with CCS
            </p>
          </div>

          <div className="hidden md:block absolute bottom-8 left-6 bg-white p-4 rounded-2xl shadow-xl border-l-4 border-yellow animate-float z-20" style={{
          animationDelay: '-4s'
        }} data-id="element-274">
            <p className="font-extrabold text-navy" data-id="element-275">Family-run since 2018</p>
          </div>
        </motion.div>
      </div>
    </section>;
}